import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { PageHeader, Card, Loading, EmptyState, Btn } from "@/components/primitives";
import { fmtDate, initials } from "@/lib/format";
import { toast } from "sonner";
import { Megaphone, Plus, Pin, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const inputCls = "w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition";

export default function Announcements() {
  const { isAdmin } = useAuth();
  const { subscribe } = useNotifications();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/announcements").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribe((evt) => { if (evt.type === "announcement.new" || evt.type === "announcement.deleted") load(); }), [subscribe, load]);

  const create = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post("/announcements", { title: title.trim(), body: body.trim(), pinned });
      toast.success("Announcement posted");
      setOpen(false); setTitle(""); setBody(""); setPinned(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Announcement deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (items === null) return <Loading />;
  const sorted = [...items].sort((a, b) => (b.pinned - a.pinned) || (new Date(b.created_at) - new Date(a.created_at)));

  return (
    <div className="max-w-3xl">
      <PageHeader title="Announcements" subtitle="Team-wide broadcasts and updates.">
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Btn data-testid="new-announcement-btn"><Plus className="w-4 h-4" /> New</Btn></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Announcement</DialogTitle>
                <DialogDescription>Broadcast a message to the whole team.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="announcement-title" className={inputCls} />
                <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message…" rows={4} data-testid="announcement-body" className={`${inputCls} h-auto py-2`} />
                <label className="flex items-center gap-2 text-sm text-curtron-ink cursor-pointer">
                  <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="announcement-pin" className="w-4 h-4 accent-[#2C4C3B]" /> Pin to top
                </label>
              </div>
              <DialogFooter>
                <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
                <Btn onClick={create} disabled={saving} data-testid="announcement-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Post</Btn>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {sorted.length === 0 ? (
        <Card><EmptyState icon={Megaphone} title="No announcements yet" /></Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((a) => (
            <Card key={a.id} className="p-4 sm:p-5" data-testid={`announcement-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-curtron-green/10 text-curtron-green flex items-center justify-center text-xs font-semibold shrink-0">{initials(a.posted_by_name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {a.pinned && <Pin className="w-3.5 h-3.5 text-curtron-ochre" />}
                    <h3 className="font-heading font-semibold text-curtron-ink">{a.title}</h3>
                  </div>
                  <p className="text-sm text-curtron-sub mt-1 whitespace-pre-wrap">{a.body}</p>
                  <div className="text-[11px] text-curtron-muted mt-2">{a.posted_by_name} · {fmtDate(a.created_at)}</div>
                </div>
                {isAdmin && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Btn variant="ghost" className="h-8 px-2 text-curtron-terracotta shrink-0" data-testid={`announcement-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Btn>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete announcement?</AlertDialogTitle><AlertDialogDescription>"{a.title}" will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(a.id)} className="bg-curtron-terracotta hover:bg-[#A35549]">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
